import { ChangeDetectionStrategy, Component, ViewEncapsulation, inject, signal, viewChild } from '@angular/core';
import { Defect, DefectSeverity, TenantUser } from '@jms/model';
import { DefectCreatorComponent, DefectService } from '@workbuddy/ui/modules/manage.job.shared/tabs/defects';
import { PAGE_COMPONENTS } from '@workbuddy/ui/blocks/page';
import { ButtonComponent } from '@workbuddy/ui/components/button';
import { ToastService } from '@workbuddy/ui/services';
import { injectDialogData, injectDialogRef } from '@workbuddy/ui/v2/blocks/dialog';
import { lastValueFrom } from 'rxjs';

export interface ItemDefectDialogData {
  /** The item the defect is raised against. `name` and `location` are shown on the defect. */
  item: { _id: string; name: string; location: string };
  /** `name` is the job number: that is what the legacy rows carried in `job.name`. */
  job: { _id: string; name: string };
  /** `name` is the stage number, matching legacy's `workOrder.name`. */
  stage: { _id: string; name: string };
  dateFormat: string;
  timezone: string;
  currentUser: TenantUser;
}

/**
 * Raise a defect against a board item, ported from the legacy items tab
 * (`embedded.codes.table.component.ts:454-500` for the draft, its template's `side-dialog` for the
 * chrome). `defect-creator` is reused rather than copied: unlike the media component it is exported
 * as a standalone component.
 *
 * The saved payload is the creator's own `form.value`, not the draft — the form drops the controls it
 * disables (`rectifiedBy` / `rectifiedAt` while `resolve` is No), which is exactly what the legacy
 * Save button submitted.
 */
@Component({
  selector: 'item-defect-dialog',
  standalone: true,
  imports: [DefectCreatorComponent, PAGE_COMPONENTS, ButtonComponent],
  templateUrl: './item.defect.dialog.component.html',
  styleUrls: ['./item.defect.dialog.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ItemDefectDialogComponent {

  private readonly dialogRef = injectDialogRef<boolean, ItemDefectDialogComponent>();
  private readonly defectService = inject(DefectService);
  private readonly toastService = inject(ToastService);

  protected readonly data = injectDialogData<ItemDefectDialogData>();
  protected readonly $creator = viewChild(DefectCreatorComponent);
  protected readonly $saving = signal(false);

  /**
   * The draft the creator patches its form from. `mode` is deliberately not bound: the legacy template
   * passed `mode="New"` but the creator never reads it.
   */
  protected readonly draft = {
    _id: null,
    job: { _id: this.data?.job?._id, name: this.data?.job?.name },
    workOrder: { _id: this.data?.stage?._id, name: this.data?.stage?.name },
    code: { _id: this.data?.item?._id, name: this.data?.item?.name, location: this.data?.item?.location },
    defectNumber: null,
    raisedBy: this.data?.currentUser as any,
    raisedAt: new Date(),
    rectifiedBy: null,
    rectifiedAt: new Date(),
    severity: DefectSeverity.Low,
    description: null,
    resolve: false,
    causedBy: null,
    defectEvidence: [],
    rectification: [],
    resolution: null,
    timezone: this.data?.timezone,
  } as Defect;

  protected async onSave(): Promise<void> {

    const defect = this.$creator()?.form?.value as Defect;

    if (!defect || this.$saving()) return;

    this.$saving.set(true);

    const result = await lastValueFrom(this.defectService.onSave(defect));

    this.$saving.set(false);

    // The service swallows the error and resolves null, so a failure is silent without this.
    if (!result) {
      this.toastService.error('Save Defect Error', 'The defect could not be saved.');
      return;
    }

    this.toastService.success('Defect added', this.data?.item?.name);

    // The item's defect count changed, so the row and the Defects tab both need a re-read.
    this.dialogRef.close(true);
  }

  protected onCancel(): void {

    this.dialogRef.close();
  }
}
